import { useCallback, useMemo } from 'react';

import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import { CopyInput } from '@/components/atoms/CopyInput/CopyInput';
import Whatsapp from '@/components/atoms/Icons/Whatsapp';
import QrCodeGenerator from '@/components/atoms/QrCodeGenerator/QrCodeGenerator';
import CardBase from '@/components/organisms/Integrations/Components/CardBase';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useWhatsapp } from '@/hooks/useWhatsapp';
import { WhatsAppIntegration } from '@/shared/types/integration';
import type { WhatsappValidationResult } from '@/shared/types/whatsapp';
import { scrollToElementById } from '@/util/dom';

import { HealthIssueItem } from './HealthIssueItem';
import { Detail, MetaLimitDetails } from './MetaLimitDetails';
import { WhatsappStatusBadge } from './StatusBadge';
import { ValidationGroup } from './ValidationGroup';
import WabaSettingsMenu from './WabaSettingsMenu';

interface Props {
  integration: WhatsAppIntegration;
  onReauthorize: () => void;
  isConnecting: boolean;
}

const partitionByBlocking = (issues: WhatsappValidationResult[]) => ({
  blocking: issues.filter((issue) => issue.blocking),
  nonBlocking: issues.filter((issue) => !issue.blocking),
});

const Connection = ({ integration, onReauthorize, isConnecting }: Props) => {
  const { t } = useTranslation();
  const { data: whatsappData, isLoading, error, dataError, uri } = useWhatsapp();

  const image = useMemo(() => {
    const circularWhatsappSvg = (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
        <circle cx="50" cy="50" r="48" fill="white" />
        <g transform="translate(10, 10)">
          <Whatsapp size={80} />
        </g>
      </svg>
    );
    const svgString = encodeURIComponent(renderToStaticMarkup(circularWhatsappSvg));
    return `data:image/svg+xml,${svgString}`;
  }, []);

  // Server returns only the visible (failed/warning) issues in `health_issues`.
  const issues = useMemo(() => whatsappData?.health_issues ?? [], [whatsappData?.health_issues]);
  const { blocking, nonBlocking } = useMemo(() => partitionByBlocking(issues), [issues]);

  const onIssueAction = useCallback(
    (issue: WhatsappValidationResult) => {
      if (issue.action === 'reauthorize') {
        onReauthorize();
      } else if (issue.action === 'open_advanced_settings') {
        // Target the encryption sub-section so the user lands on the action they need
        // to take (re-uploading the key), not the top of the broader Advanced card.
        scrollToElementById('whatsapp-encryption-section', { block: 'start' });
      }
    },
    [onReauthorize]
  );

  const renderIssue = (issue: WhatsappValidationResult) => (
    <HealthIssueItem
      key={issue.id}
      issue={issue}
      isConnecting={isConnecting}
      onAction={() => onIssueAction(issue)}
    />
  );

  return (
    <CardBase
      title={t('integrations.whatsapp.con.title')}
      titleVariant="secondary"
      status={integration?.status}
      active={integration?.active}
    >
      {blocking.length > 0 && (
        <ValidationGroup blocking issues={blocking} renderIssue={renderIssue} />
      )}
      {nonBlocking.length > 0 && (
        <ValidationGroup blocking={false} issues={nonBlocking} renderIssue={renderIssue} />
      )}

      {dataError && issues.length === 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{t('integrations.whatsapp.data_fetch_error')}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="flex gap-6">
          <div className="flex-1 space-y-2">
            {isLoading ? (
              <>
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-36" />
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-10 w-10 rounded-lg" />
                </div>
              </>
            ) : (
              <>
                <Detail label={t('integrations.whatsapp.account_name')}>
                  <p className="font-medium">{whatsappData?.verified_name || '-'}</p>
                </Detail>

                <Detail label={t('integrations.whatsapp.phone_disp')}>
                  <p className="font-medium">{whatsappData?.display_phone_number || '-'}</p>
                </Detail>

                {whatsappData?.quality_rating && whatsappData.quality_rating !== 'UNKNOWN' && (
                  <Detail label={t('integrations.whatsapp.quality_rating.label')}>
                    <WhatsappStatusBadge value={whatsappData.quality_rating} />
                  </Detail>
                )}

                <MetaLimitDetails
                  messagingLimit={whatsappData?.whatsapp_business_manager_messaging_limit}
                  nameStatus={whatsappData?.name_status}
                  throughputLevel={whatsappData?.throughput?.level}
                />

                <Detail label={t('integrations.whatsapp.connection_mode.label')}>
                  <WhatsappStatusBadge
                    value={whatsappData?.is_on_biz_app ? 'biz_app' : 'api_only'}
                  />
                </Detail>

                <div className="flex flex-col gap-2">
                  <span className="text-sm text-foreground-muted">
                    {t('integrations.whatsapp.display_image')}:
                  </span>
                  <Avatar size="md" isRounded={false} className="size-10">
                    <AvatarImage
                      src={whatsappData?.profile_picture_url}
                      alt={whatsappData?.verified_name}
                    />
                    <AvatarFallback size="md">
                      {whatsappData?.verified_name?.charAt(0).toUpperCase() || 'W'}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </>
            )}
          </div>

          <div className="flex-shrink-0">
            {isLoading ? (
              <Skeleton className="h-24 w-24 rounded-lg" />
            ) : (
              uri && !error && <QrCodeGenerator url={uri} image={image} size="md" />
            )}
          </div>
        </div>

        {uri && (
          <div className="space-y-1.5">
            <Label>{t('integrations.whatsapp.deep_link')}</Label>
            <CopyInput value={uri} />
          </div>
        )}

        <div className="border-t border-stroke pt-2">
          <WabaSettingsMenu />
        </div>
      </div>
    </CardBase>
  );
};

export default Connection;
